// src/components/patient/tickets/ticketUi.jsx
//
// Shared pieces for the patient ticket pages: badges, the attachment policy
// check and the pending-upload list.
//
// Files are uploaded as soon as they are chosen (presign -> PUT) and kept in a
// pending list with their opaque upload token; the ticket/reply submit only
// sends the tokens. A failed submit keeps the list intact.

import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { readApiError } from "../../../lib/apiErrors";
import { Pill, classNames } from "../booking/bookingUi";

export const CATEGORY_TONES = { appointment: "info", invoice: "violet", other: "neutral" };
export const PRIORITY_TONES = { low: "neutral", medium: "info", high: "warn", urgent: "danger" };
export const STATUS_TONES = { open: "success", in_progress: "info", resolved: "violet", closed: "neutral" };

export const FALLBACK_VOCAB = {
  categories: [
    { value: "appointment", label: "Appointment" },
    { value: "invoice", label: "Invoice" },
    { value: "other", label: "Other" },
  ],
  priorities: [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ],
  statuses: [
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ],
};

export const DEFAULT_ATTACHMENT_POLICY = {
  max_file_bytes: 10 * 1024 * 1024,
  max_files: 5,
  max_total_bytes: 25 * 1024 * 1024,
  allowed_extensions: [".png", ".jpg", ".jpeg", ".webp", ".pdf", ".txt", ".doc", ".docx"],
};

export function CategoryBadge({ value, label }) {
  return <Pill tone={CATEGORY_TONES[value] || "neutral"}>{label || value}</Pill>;
}
export function PriorityBadge({ value, label }) {
  return <Pill tone={PRIORITY_TONES[value] || "neutral"}>{label || value}</Pill>;
}
export function StatusBadge({ value, label }) {
  return <Pill tone={STATUS_TONES[value] || "neutral"}>{label || value}</Pill>;
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(name) {
  const match = String(name || "").toLowerCase().match(/\.[a-z0-9]+$/);
  return match ? match[0] : "";
}

/** A readable reason the file cannot be attached, or "". `current` = files already pending. */
export function attachmentProblem(file, current, policy) {
  const p = { ...DEFAULT_ATTACHMENT_POLICY, ...(policy || {}) };
  const allowed = (p.allowed_extensions || []).map((ext) => String(ext).toLowerCase());
  if (allowed.length && !allowed.includes(extensionOf(file.name))) {
    return `${file.name}: this file type is not allowed. Use ${allowed.map((ext) => ext.replace(".", "").toUpperCase()).join(", ")}.`;
  }
  if (!file.size) return `${file.name}: the file is empty.`;
  if (p.max_file_bytes && file.size > p.max_file_bytes) return `${file.name}: files can be up to ${formatBytes(p.max_file_bytes)}.`;
  if (p.max_files && current.length + 1 > p.max_files) return `You can attach up to ${p.max_files} files.`;
  const used = current.reduce((sum, item) => sum + (Number(item.size) || 0), 0);
  if (p.max_total_bytes && used + file.size > p.max_total_bytes) return `Attachments can total up to ${formatBytes(p.max_total_bytes)}.`;
  return "";
}

let localCounter = 0;

/**
 * Pending uploads. `upload(file)` resolves to an opaque upload token.
 * -> { items, add(fileList), remove(localId), retry(localId), reset(), tokens, busy, notice }
 */
export function usePendingUploads(upload, policy) {
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState("");
  const itemsRef = useRef(items);
  const uploadRef = useRef(upload);
  const mounted = useRef(true);
  itemsRef.current = items;
  uploadRef.current = upload;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const start = useCallback((entry) => {
    Promise.resolve()
      .then(() => uploadRef.current(entry.file))
      .then((token) => {
        if (!mounted.current) return;
        setItems((prev) => prev.map((item) => (item.localId === entry.localId ? { ...item, status: "ready", token, error: "" } : item)));
      })
      .catch((err) => {
        if (!mounted.current) return;
        setItems((prev) =>
          prev.map((item) => (item.localId === entry.localId ? { ...item, status: "failed", error: readApiError(err, "The file could not be uploaded.") } : item))
        );
      });
  }, []);

  const add = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const accepted = [];
      const problems = [];
      let current = itemsRef.current.slice();
      files.forEach((file) => {
        const problem = attachmentProblem(file, current, policy);
        if (problem) {
          problems.push(problem);
          return;
        }
        localCounter += 1;
        const entry = { localId: `f${localCounter}`, file, name: file.name, size: file.size, status: "uploading", token: null, error: "" };
        accepted.push(entry);
        current = current.concat(entry);
      });
      setNotice(problems.join(" "));
      if (!accepted.length) return;
      itemsRef.current = current;
      setItems(current);
      accepted.forEach(start);
    },
    [policy, start]
  );

  const remove = useCallback((localId) => {
    setItems((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const retry = useCallback(
    (localId) => {
      const entry = itemsRef.current.find((item) => item.localId === localId);
      if (!entry) return;
      setItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, status: "uploading", error: "" } : item)));
      start(entry);
    },
    [start]
  );

  const reset = useCallback(() => {
    setItems([]);
    setNotice("");
  }, []);

  const tokens = items.filter((item) => item.status === "ready" && item.token).map((item) => item.token);
  const busy = items.some((item) => item.status === "uploading");
  const failed = items.some((item) => item.status === "failed");
  return { items, add, remove, retry, reset, tokens, busy, failed, notice };
}

const STATUS_TEXT = { uploading: "Uploading…", ready: "Ready", failed: "Upload failed" };

export function AttachmentPicker({ uploads, disabled = false, title, hint, policy, idPrefix }) {
  const autoId = useId();
  const base = idPrefix || autoId;
  const inputRef = useRef(null);
  const p = { ...DEFAULT_ATTACHMENT_POLICY, ...(policy || {}) };
  const accept = p.accept || (p.allowed_extensions || []).join(",");
  const full = p.max_files ? uploads.items.length >= p.max_files : false;

  return (
    <div className="rounded-2xl border border-dashed border-border bg-slate-50 p-3" data-testid={`${base}-attachments`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          {title ? <div className="text-sm font-semibold text-text">{title}</div> : null}
          <p id={`${base}-hint`} className="text-xs text-muted">
            {hint ? `${hint} ` : ""}
            Up to {p.max_files} files, {formatBytes(p.max_file_bytes)} each.
          </p>
        </div>
        <input ref={inputRef} id={`${base}-file-input`} type="file" multiple accept={accept} className="sr-only" disabled={disabled || full}
          aria-describedby={`${base}-hint`} data-testid={`${base}-file-input`}
          onChange={(e) => {
            uploads.add(e.target.files);
            e.target.value = "";
          }} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled || full}
          className="rounded-2xl border border-border bg-white px-3 py-1.5 text-sm font-semibold text-text hover:bg-slate-50 disabled:opacity-50">
          Choose files
        </button>
      </div>
      {uploads.notice ? <p role="alert" className="mt-2 text-xs font-semibold text-rose-700">{uploads.notice}</p> : null}
      {uploads.items.length ? (
        <ul className="mt-2 space-y-1.5" aria-label="Attached files">
          {uploads.items.map((item) => (
            <li key={item.localId} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="pending-file">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-text">{item.name}</span>
                <span className="ml-2 text-xs text-muted">{formatBytes(item.size)}</span>
              </span>
              <span className={classNames("text-xs font-semibold", item.status === "failed" ? "text-rose-700" : item.status === "ready" ? "text-emerald-700" : "text-muted")} role={item.status === "failed" ? "alert" : undefined}>
                {item.status === "failed" && item.error ? item.error : STATUS_TEXT[item.status]}
              </span>
              <span className="flex gap-1">
                {item.status === "failed" ? (
                  <button type="button" onClick={() => uploads.retry(item.localId)} disabled={disabled} className="rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10">Retry</button>
                ) : null}
                <button type="button" onClick={() => uploads.remove(item.localId)} disabled={disabled} aria-label={`Remove ${item.name}`} className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">Remove</button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
